/* Where you file, and what that means for the arithmetic.
 *
 * The rate was a constant — 19%, correct for one country and silently wrong for
 * every other. It is a setting now, and picking a country fills it in.
 *
 * One country is filled in: the one whose rules are actually known here. Every
 * other choice asks for the rate rather than supplying one, and says so. A
 * table of half-remembered foreign rates would be the same mistake this book
 * refuses everywhere else — a figure that looks authoritative and is not
 * measured — except that here the reader would carry it onto a tax return. A
 * rate the user typed is a rate the user can check. Adding a country means
 * knowing its rules, not guessing them.
 *
 * None of this is advice (§1). It applies a percentage the user set to figures
 * the user recorded, and every screen that shows the result says so. */

export interface Regime {
  code: string;
  name: string;
  /** Flat rate on realised gains, as a percentage. Null when this app does not
      know the country's rules and the user has to set the figure. */
  gainsPct: number | null;
  dividendPct: number | null;
  /** Whether tax already withheld abroad comes off the charge on dividends. */
  creditsWithholding: boolean;
  /** One sentence, shown under the choice. Plain language (§4.2). */
  note: string;
}

export const REGIMES: Regime[] = [
  {
    code: 'PL',
    name: 'Poland',
    gainsPct: 19,
    dividendPct: 19,
    creditsWithholding: true,
    note: 'Gains and dividends are taxed at a flat 19%. Tax withheld abroad is credited against '
      + 'the dividend charge, and an IKE account sits outside all of it, so mark it sheltered '
      + 'under Brokers and it stays out of every total here.',
  },
  {
    code: 'other',
    name: 'Somewhere else · set the rates yourself',
    gainsPct: null,
    dividendPct: null,
    creditsWithholding: false,
    note: 'This app knows one country’s rules. Anywhere else, type the rates that apply to '
      + 'you: a rate guessed on your behalf would look as confident as a correct one and would '
      + 'end up on a tax return.',
  },
];

export function regimeOf(code: string | undefined): Regime {
  return REGIMES.find((r) => r.code === code) ?? REGIMES[REGIMES.length - 1]!;
}

export interface TaxRates {
  gainsPct: number | null;
  dividendPct: number | null;
  creditsWithholding: boolean;
  /** True when the figure came from the country rather than from a field the
      user typed — the screens say which, so a number is never mistaken for
      something the app worked out. */
  fromRegime: boolean;
}

/** The rates in force: what the country sets, unless the user has overridden
    it. An override of zero is a rate, not an absence, so it is honoured. */
export function ratesFor(settings: {
  taxCountry?: string;
  taxGainsPct?: number;
  taxDividendPct?: number;
}): TaxRates {
  const regime = regimeOf(settings.taxCountry);
  const gainsSet = typeof settings.taxGainsPct === 'number' && Number.isFinite(settings.taxGainsPct);
  const divSet = typeof settings.taxDividendPct === 'number' && Number.isFinite(settings.taxDividendPct);

  return {
    gainsPct: gainsSet ? settings.taxGainsPct! : regime.gainsPct,
    dividendPct: divSet ? settings.taxDividendPct! : regime.dividendPct,
    creditsWithholding: regime.creditsWithholding,
    fromRegime: !gainsSet && !divSet && regime.gainsPct !== null,
  };
}

/* ---------- what a year comes to, at those rates ---------- */

export interface TaxDue {
  /** The charge on realised gains. Null when no rate is set — which is not the
      same as nothing being due, and the screens print it as "no rate set"
      rather than as a zero. */
  onGains: number | null;
  onDividends: number | null;
  /** Withholding credited against the dividend charge, capped at that charge:
      a credit larger than the liability is not a refund from this app. */
  credited: number;
  /** What is left after the credit. Null when either rate is missing. */
  total: number | null;
}

export function taxDue(input: {
  realisedGain: number;
  dividendGross: number;
  dividendWithheld: number;
  rates: TaxRates;
}): TaxDue {
  const { realisedGain, dividendGross, dividendWithheld, rates } = input;

  // A loss is not a negative charge. What a loss can be carried against is a
  // question about a whole return, not about this year's figures alone.
  const onGains = rates.gainsPct === null
    ? null
    : Math.max(0, realisedGain) * (rates.gainsPct / 100);
  const onDividends = rates.dividendPct === null
    ? null
    : Math.max(0, dividendGross) * (rates.dividendPct / 100);

  const credited = rates.creditsWithholding && onDividends !== null
    ? Math.min(dividendWithheld, onDividends)
    : 0;

  const total = onGains === null || onDividends === null
    ? null
    : onGains + onDividends - credited;

  return { onGains, onDividends, credited, total };
}
